import { FeathersError } from '@feathersjs/errors';
import { AttendanceData } from '@tymbe/schema/attendance.interface';
import { AttendanceResolution } from '@tymbe/schema/enums';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import ShiftUnapprovedAttendance, {
  AttendanceScope,
  FormValues,
} from './components/ShiftAttendance/ShiftUnapprovedAttendance';
import apiClient from '../../apiClient';
import { useUser } from '../../apiClient/ApiContext';
import { ErrorAlert, SuccessAlert } from '../../components/alerts';
import Spinner from '../../components/Spinner';
import { ShiftData } from '../../types/TymbeApi';
import { calculateWorkTime } from '../../utils/time';
import useSearchString from '../../hooks/UrlParamsHandler/useSearchParams';
import { FavouriteIcon } from '@tymbe/ui-icons';
import CompanyInfo from './components/ShiftDetail/CompanyInfo';
import ShiftTemplateBox from './components/ShiftTemplateBox';
import { useRef } from 'react';
import ShiftResolvedAttendance from './components/ShiftAttendance/ShiftResolvedAttendance';
import moment from 'moment';
import { Form, FormApi } from 'informed';
import AttendanceStats from './components/ShiftAttendance/AttendanceStats';
import ConfirmAttendanceButton from './components/ConfirmAttendanceButton';
import { isDeleted, notDeleted } from './components/utils';

type ShiftAttendancePageProps = {
  onSuccessCallback?: () => Promise<unknown>;
};

const ShiftAttendancePage = ({onSuccessCallback}: ShiftAttendancePageProps) => {
  const user = useUser();
  const formApiRef = useRef<FormApi<FormValues>>(undefined);

  const onSubmitSelected = () => {
    if (!formApiRef.current) return;
    const { values } = formApiRef.current.getFormState();
    const selectedItems = Object.values(values.applications).filter(item => item.selected);

    onSubmit(selectedItems);
  };
  const [searchParams] = useSearchString();
  const shiftId = searchParams.get('shiftAttendance') || undefined;
  const queryClient = useQueryClient();
  const queryKey: [string, string, number] = [
    'shift',
    'application',
    Number(shiftId),
  ];

  const { data: shift } = useQuery(
    queryKey,
    async ({ queryKey: key }) =>
      apiClient.service('shift').get(key[2], {
        query: {
          $eager:
            '[company, branchoffice.parent, shiftTemplate, perk, manShift.[activeApplication.[person.personData], perk], application.[person.personData, attendance.confirmedBy]]',
          $appendJuniorProp: true,
        },
      }),
    { enabled: !!shiftId }
  );

  const { mutateAsync: createAttendance, isLoading } = useMutation({
    mutationKey: ['createAttendance'],
    mutationFn: async (values: Partial<AttendanceData>[]) =>
      apiClient.service('attendance').create(values),
    onMutate: async (values) => {
      await queryClient.cancelQueries({ queryKey });

      const previousShift = queryClient.getQueryData<ShiftData>(queryKey);

      if (!previousShift) return {};

      return { previousShift };
    },
    onError: (err) => {
      if (err instanceof FeathersError) {
        ErrorAlert(`${err.name}: ${err.message}`, 10);
      } else {
        ErrorAlert('Při vytváření docházky došlo k neznámé chybě');
        console.error(err);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKey);
      await onSuccessCallback?.();
      SuccessAlert('Docházka byla úspěšně vytvořena');
    },
  });

  const onSubmit = async (values: AttendanceScope[]) => {
    const attendances = values?.map(
      (vals): Partial<AttendanceData> => ({
        confirmed_time:
          vals.resolution === AttendanceResolution.OK
            ? calculateWorkTime(
                moment(vals.start, 'H:mm'),
                moment(vals.end, 'H:mm'),
                false,
                'minute'
              )
            : 0,
        resolution: vals.resolution,
        application_id: vals.id,
        confirmed_by: user.person_id,
        job_evaluation:
          vals.resolution === AttendanceResolution.OK
            ? Number(vals.rating)
            : null,
      })
    );
    await createAttendance(attendances);
  };

  if (!shiftId) return null;
  if (!shift) return <Spinner show className='size-6 m-auto' />;

  return (
    <Form
      formApiRef={formApiRef}
      className="flex flex-col flex-1 overflow-hidden h-full"
    >
      <div className="flex flex-1 overflow-y-auto p-4 pb-0">
        <div className="h-full flex flex-col gap-3">
          <div className="flex items-center gap-2 text-caption-xl truncate">
            {shift.company?.name}
            <FavouriteIcon
              name="chevron_right"
              className="size-4 p-0.5 text-secondary-400 bg-secondary-transparent-5 rounded-sm"
            />
            <span className="truncate">{shift.branchoffice?.parent?.name}</span>
            <FavouriteIcon
              name="chevron_right"
              className="size-4 p-0.5 text-secondary-400 bg-secondary-transparent-5 rounded-sm"
            />
            <span className="truncate">{shift.branchoffice?.name}</span>
          </div>
          <div className="bg-secondary-50 p-6 rounded-3xl rounded-b-none h-full gap-6 flex flex-col">
            <ShiftTemplateBox shift={shift} />
            <CompanyInfo shift={shift} />
          </div>
        </div>

        <div className="w-full pr-4 pl-10 overflow-y-auto flex flex-col flex-1 gap-4">
          <ShiftUnapprovedAttendance
            applications={
              shift.application?.filter(
                (app) => !app.attendance || isDeleted(app.attendance)
              ) ?? []
            }
            startDate={shift.start_time}
            endDate={shift.end_time}
            onSubmit={onSubmit}
            isLoading={isLoading}
          />
          <ShiftResolvedAttendance
            applications={
              shift.application?.filter(
                (app) => app.attendance && notDeleted(app.attendance)
              ) ?? []
            }
          />
          <AttendanceStats shift={shift} />
        </div>
      </div>
      <div className="text-secondary-600 flex py-3 px-10 items-center border-b justify-end bg-secondary-50 border-secondary-100 min-h-16">
        <ConfirmAttendanceButton onSubmitSelected={onSubmitSelected} />
      </div>
    </Form>
  );
};

export default ShiftAttendancePage;
